import { useCallback, useEffect, useState } from 'react';

import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import AdminTableDeleteButton from '../AdminTableDeleteButton';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import {
  FormateurDocumentsCard,
  FormateurImpactCard,
  FormateurProfilReadonlyCard,
  FormateurProfilSyntheseCard,
} from './FormateurFicheSections';
import FormateurTarifsEditor from './FormateurTarifsEditor';
import {
  parseDiplomes,
  parseDisponibilites,
  parseReferencements,
} from '../../../types/formateurProfilExtras';
import {
  createAdminFormateur,
  deleteAdminFormateur,
  fetchAdminFormateurDocuments,
  fetchAdminFormateurTarifs,
  fetchAdminFormateurs,
  fetchFormateurDetail,
  fetchPrestationsFormateur,
  downloadFormateurRemunerationPdf,
  markPrestationsPayees,
  markPrestationsRealisees,
  markPrestationComponentsPayees,
  payPrestationComponentsViaWallet,
  payPrestationsViaWallet,
  putAdminFormateurTarifs,
  resendFormateurAccess,
  syncPrestationsFromSessions,
  validateAdminFormateurDocument,
  validateAdminFormateurReferencement,
} from '../../../services/academiaAdminService';
import {
  FORMATEUR_SPECIALITE_OPTIONS,
} from '../../../constants/formateurSpecialites';
import { isAdministrateur } from '../../../utils/adminAccess';
import {
  buildFormateurTarifsDraftFromApi,
  emptyFormateurTarifsDraft,
  formateurTarifsDraftToPayload,
  type FormateurTarifsDraft,
} from '../../../utils/formateurTarifsDraft';
import AcademiaSelect from '../../common/AcademiaSelect';

const FIELD_LABEL = 'text-xs font-bold text-on-surface-variant';
const FIELD_LABEL_BLOCK = 'block min-w-0 text-sm';
const INPUT_CLASS =
  'w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

type FormateurRow = {
  id: string;
  nomComplet: string;
  telephone: string;
  email?: string;
  specialite?: string;
  noteMoyenne: number;
  nombreEvaluations: number;
  tauxSatisfactionPct?: number | null;
  sessionsAssignees: number;
  nombrePrestations: number;
  estActif: boolean;
};

type TabId = 'liste' | 'fiche' | 'prestations';

type ToastState = { message: string; variant: 'success' | 'error' } | null;

type PanelProps = {
  user?: { role?: string } | null;
};

export default function AcademiaFormateursPanel({ user }: PanelProps) {
  const canDelete = isAdministrateur(user);
  const [tab, setTab] = useState<TabId>('liste');
  const [rows, setRows] = useState<FormateurRow[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [prestations, setPrestations] = useState<
    {
      id: string;
      statut: string;
      datePrestation: string;
      heuresPrestees: number;
      montantTotalCdf: number;
      montantVacationCdf?: number;
      primeTransportCdf: number;
      vacationPayee?: boolean;
      transportPaye?: boolean;
      modePaiementVacationLabel?: string | null;
      modePaiementTransportLabel?: string | null;
      formateurUser?: { profil?: { prenom?: string; nom?: string } };
      session?: { vacation?: { libelle?: string }; filiere?: { libelle?: string } };
    }[]
  >([]);
  const [documents, setDocuments] = useState<
    { id: string; typeDocument: string; statutValidation: string; urlFichier: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);
  const [error, setError] = useState('');

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const runAction = useCallback(
    async (action: () => Promise<string | void>, errorFallback = 'Action impossible.') => {
      try {
        const successMessage = await action();
        showToast(successMessage || 'Action réussie.', 'success');
      } catch (e) {
        const message = e instanceof Error ? e.message : errorFallback;
        showToast(message, 'error');
      }
    },
    [showToast],
  );
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [horsWalletMode, setHorsWalletMode] = useState<'ESPECES' | 'VIREMENT_BANCAIRE'>('ESPECES');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({
    prenom: '',
    nom: '',
    telephone: '',
    email: '',
    specialite: 'Général',
  });
  const [tarifsDraft, setTarifsDraft] = useState<FormateurTarifsDraft>(emptyFormateurTarifsDraft());
  const [savingTarifs, setSavingTarifs] = useState(false);
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchAdminFormateurs());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useRegisterPortalRefresh(() => void loadList());

  const loadDetail = async (id: string) => {
    setLoading(true);
    setError('');
    try {
      const [data, tarifsPayload] = await Promise.all([
        fetchFormateurDetail(id),
        fetchAdminFormateurTarifs(id).catch(() => null),
      ]);
      setDetail(data);
      setSelectedId(id);
      setTarifsDraft(
        tarifsPayload ? buildFormateurTarifsDraftFromApi(tarifsPayload) : emptyFormateurTarifsDraft(),
      );
      setDocuments(await fetchAdminFormateurDocuments(id));
      setTab('fiche');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur fiche');
    } finally {
      setLoading(false);
    }
  };

  const saveFormateurTarifs = async () => {
    if (!selectedId) return;
    setSavingTarifs(true);
    try {
      const response = await putAdminFormateurTarifs(selectedId, formateurTarifsDraftToPayload(tarifsDraft));
      showToast(response?.message ?? 'Rémunération enregistrée', 'success');
      await refreshDetailSynthese();
      const tarifsPayload = await fetchAdminFormateurTarifs(selectedId);
      setTarifsDraft(buildFormateurTarifsDraftFromApi(tarifsPayload));
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Enregistrement impossible', 'error');
    } finally {
      setSavingTarifs(false);
    }
  };

  const loadPrestations = async (idFormateur?: string) => {
    try {
      setPrestations(await fetchPrestationsFormateur(idFormateur));
    } catch {
      setPrestations([]);
    }
  };

  const refreshDetailSynthese = async () => {
    if (!selectedId || !detail) return;
    try {
      setDetail(await fetchFormateurDetail(selectedId));
    } catch {
      /* fiche optionnelle */
    }
  };

  const prestationIdsEligibles = prestations
    .filter((p) => p.statut === 'PLANIFIEE' || p.statut === 'REALISEE')
    .map((p) => p.id);

  const prestationIdsForComposante = (composante: 'vacation' | 'transport') =>
    prestations
      .filter((p) => {
        if (p.statut !== 'PLANIFIEE' && p.statut !== 'REALISEE') return false;
        return composante === 'vacation' ? !p.vacationPayee : !p.transportPaye;
      })
      .map((p) => p.id);

  const reloadPrestationsEtSynthese = async () => {
    await loadPrestations();
    await refreshDetailSynthese();
  };

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (tab === 'prestations') void loadPrestations();
  }, [tab]);

  const pf = detail?.profilFormateur as Record<string, unknown> | undefined;
  const profil = detail?.profil as { prenom?: string; nom?: string; photoUrl?: string; urlAvatar?: string } | undefined;
  const utilisateur = detail?.utilisateur as { email?: string; telephone?: string } | undefined;
  const photoUrl = profil?.photoUrl ?? profil?.urlAvatar ?? '';
  const viewDiplomes = parseDiplomes(pf?.diplomes);
  const viewDisponibilites = parseDisponibilites(pf?.disponibilites);
  const viewReferencements = parseReferencements(pf?.referencements);
  const perf = detail?.performance as Record<string, unknown> | undefined;
  const synthese = detail?.synthesePaiement as Record<string, unknown> | undefined;
  const tarif = synthese?.tarifActif as
    | {
        referenceUsdHoraire?: number;
        montantHoraireCdf?: number;
        primeTransportJourCdf?: number;
        referenceUsdTransport?: number;
        personnalise?: boolean;
      }
    | undefined;
  const impact = detail?.impactAcademique as {
    cohortes?: {
      code: string;
      libelle?: string;
      moyenneCohorteSur20: number | null;
      tauxPresenceMoyenPct: number | null;
      nombreApprenants: number;
    }[];
    synthese?: { moyenneGlobaleSur20: number | null };
  } | undefined;
  const evaluations = (detail?.evaluations as {
    id: string;
    notePonctualite: number;
    notePedagogie: number;
    apprenantLabel?: string;
    dateCreation: string;
    commentaire?: string | null;
  }[]) ?? [];
  const indicateurs = detail?.indicateursOperationnels as
    | { tauxSaisiePresencesPct: number | null; seancesPassees: number; seancesAvecPresencesSaisies: number }
    | undefined;

  const selectedPrestationIds = prestationIdsEligibles;

  return (
    <AdminPageShell
      title="Gestion des formateurs"
      description="Identité, CV, prestations, rémunération (10 USD/h + 10 USD transport/jour) et performances pédagogiques."
      sideCard={{
        label: 'Formateurs actifs',
        value: String(rows.filter((r) => r.estActif).length),
        helper: 'Grille globale ou personnalisée par formateur',
        sideLinks: [
          { label: 'Planning', to: '/admin/planning', icon: 'calendar_month' },
          { label: 'Tarifs formateur', to: '/admin/tarifs', icon: 'payments' },
        ],
      }}
      isLoading={loading}
      loadingMessage="Chargement…"
    >
      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />
      {error ? (
        <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {error}
        </p>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        {(
          [
            ['liste', 'Annuaire'],
            ['fiche', 'Fiche formateur (lecture)'],
            ['prestations', 'Prestations & paiements'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-bold ${
              tab === id ? 'bg-primary text-white' : 'border bg-white'
            }`}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          className="ml-auto rounded-lg border border-primary px-3 py-1.5 text-sm font-bold text-primary"
          onClick={() => {
            setShowCreate((v) => {
              if (!v) {
                dismissToast();
                setCreateForm({
                  prenom: '',
                  nom: '',
                  telephone: '',
                  email: '',
                  specialite: 'Général',
                });
              }
              return !v;
            });
          }}
        >
          + Nouveau formateur
        </button>
      </div>

      {showCreate ? (
        <div className="mb-6 space-y-3 rounded-xl border p-4">
          <p className="text-sm text-on-surface-variant">
            L&apos;admin saisit l&apos;identité minimale. Un mot de passe provisoire à 6 chiffres est envoyé par
            e-mail ; le formateur complète son profil puis choisit son mot de passe personnel.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(
              [
                ['prenom', 'Prénom', 'text'],
                ['nom', 'Nom', 'text'],
                ['telephone', 'Téléphone', 'tel'],
                ['email', 'E-mail *', 'email'],
              ] as const
            ).map(([key, label, type]) => (
              <label key={key} className={FIELD_LABEL_BLOCK}>
                <span className={FIELD_LABEL}>{label}</span>
                <input
                  type={type}
                  autoComplete="off"
                  className={`${INPUT_CLASS} mt-1`}
                  placeholder={key === 'telephone' ? 'Optionnel' : undefined}
                  value={createForm[key]}
                  onChange={(e) => setCreateForm((f) => ({ ...f, [key]: e.target.value }))}
                />
              </label>
            ))}
            <label className={FIELD_LABEL_BLOCK}>
              <span className={FIELD_LABEL}>Spécialité</span>
              <AcademiaSelect
                fieldSize="sm"
                className="mt-1 w-full"
                menuPlacement="top"
                value={createForm.specialite}
                onChange={(e) => setCreateForm((f) => ({ ...f, specialite: e.target.value }))}
                aria-label="Spécialité du formateur"
              >
                {FORMATEUR_SPECIALITE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </AcademiaSelect>
            </label>
          </div>
          <div className="mt-3">
            <button
              type="button"
              disabled={creating}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
              onClick={() => {
                if (!createForm.email.trim()) {
                  showToast('E-mail requis', 'error');
                  return;
                }
                void (async () => {
                  setCreating(true);
                  try {
                    const successMessage = await (async () => {
                      const r = await createAdminFormateur(createForm);
                      setShowCreate(false);
                      await loadList();
                      return r?.message ?? 'Formateur créé — e-mail envoyé';
                    })();
                    showToast(successMessage, 'success');
                  } catch (e) {
                    const message = e instanceof Error ? e.message : 'Échec de la création du formateur';
                    showToast(message, 'error');
                  } finally {
                    setCreating(false);
                  }
                })();
              }}
            >
              {creating ? 'Envoi…' : 'Inviter par e-mail'}
            </button>
          </div>
        </div>
      ) : null}

      {tab === 'liste' ? (
        <table className="min-w-full text-sm">
          <thead className="bg-surface-container-low text-xs uppercase">
            <tr>
              <th className="px-4 py-2 text-left">Formateur</th>
              <th className="px-4 py-2 text-left">Spécialité</th>
              <th className="px-4 py-2 text-right">Note</th>
              <th className="px-4 py-2 text-right">Sessions</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-2">
                  <span className="font-semibold">{r.nomComplet}</span>
                  <span className="block font-mono text-xs text-on-surface-variant">{r.telephone}</span>
                  {r.email ? (
                    <span className="block text-xs text-on-surface-variant break-all">{r.email}</span>
                  ) : null}
                </td>
                <td className="px-4 py-2">{r.specialite}</td>
                <td className="px-4 py-2 text-right">
                  {r.noteMoyenne.toFixed(1)}/5
                  {r.tauxSatisfactionPct != null ? (
                    <span className="block text-xs text-on-surface-variant">
                      {r.tauxSatisfactionPct}% satisfaction
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-2 text-right">{r.sessionsAssignees}</td>
                <td className="px-4 py-2 text-right">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary transition hover:bg-primary/10"
                      onClick={() => void loadDetail(r.id)}
                    >
                      <LucideSymbolIcon name="visibility" tone="inherit" size={16} />
                      Ouvrir
                    </button>
                    <AdminTableDeleteButton
                      canDelete={canDelete}
                      disabled={deletingId === r.id}
                      confirmMessage={`Supprimer définitivement le formateur ${r.nomComplet} ? Cette action est irréversible.`}
                      onDelete={async () => {
                        setDeletingId(r.id);
                        try {
                          await deleteAdminFormateur(r.id);
                          if (selectedId === r.id) {
                            setSelectedId('');
                            setDetail(null);
                            setTab('liste');
                          }
                          await loadList();
                        } catch (e) {
                          showToast(e instanceof Error ? e.message : 'Suppression impossible.', 'error');
                        } finally {
                          setDeletingId(null);
                        }
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {tab === 'fiche' && detail ? (
        <div className="formateur-fiche-stack space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border p-3">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Ponctualité</p>
              <p className="text-xl font-black">{Number(perf?.notePonctualiteMoyenne ?? 0).toFixed(1)}/5</p>
            </div>
            <div className="rounded-xl border p-3">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Pédagogie</p>
              <p className="text-xl font-black">{Number(perf?.notePedagogieMoyenne ?? 0).toFixed(1)}/5</p>
            </div>
            <div className="rounded-xl border p-3">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Évaluations</p>
              <p className="text-xl font-black">{String(perf?.nombreEvaluations ?? 0)}</p>
            </div>
            {indicateurs?.tauxSaisiePresencesPct != null ? (
              <div className="rounded-xl border p-3">
                <p className="text-xs font-bold uppercase text-on-surface-variant">Saisie présences</p>
                <p className="text-xl font-black">{indicateurs.tauxSaisiePresencesPct}%</p>
                <p className="text-[10px] text-on-surface-variant">
                  {indicateurs.seancesAvecPresencesSaisies}/{indicateurs.seancesPassees} séances
                </p>
              </div>
            ) : null}
            <div className="rounded-xl border p-3">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Montant dû</p>
              <p className="text-xl font-black text-primary">
                {Number(synthese?.montantDuCdf ?? 0).toLocaleString('fr-FR')} CDF
              </p>
            </div>
            <div className="rounded-xl border p-3">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Vacation (heures)</p>
              <p className="text-xl font-black">
                {Number(synthese?.montantVacationDuCdf ?? 0).toLocaleString('fr-FR')} CDF
              </p>
              <p className="text-[10px] text-on-surface-variant">
                {Number(synthese?.heuresPrestees ?? 0)} h dues
              </p>
            </div>
            <div className="rounded-xl border p-3">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Transport</p>
              <p className="text-xl font-black">
                {Number(synthese?.montantTransportDuCdf ?? 0).toLocaleString('fr-FR')} CDF
              </p>
              <p className="text-[10px] text-on-surface-variant">
                {Number(synthese?.joursPrestes ?? 0)} jour(s)
              </p>
            </div>
          </div>

          <FormateurProfilSyntheseCard
            email={utilisateur?.email}
            telephone={utilisateur?.telephone}
            specialite={String(pf?.specialite ?? '')}
            experienceAnnees={Number(pf?.experienceAnnees ?? 0)}
            biographie={String(pf?.biographie ?? '')}
            urlCv={pf?.urlCv ? String(pf.urlCv) : undefined}
            cvApiPath={
              selectedId && pf?.urlCv
                ? `/admin/formateurs/${encodeURIComponent(selectedId)}/cv`
                : null
            }
            tarif={tarif}
            onResendAccess={() =>
              void runAction(
                async () => {
                  const r = await resendFormateurAccess(selectedId);
                  return r?.message ?? 'Nouveau mot de passe envoyé par e-mail';
                },
                'Échec de l’envoi de l’e-mail d’accès',
              )
            }
            onSyncPrestations={() =>
              void runAction(async () => {
                const r = await syncPrestationsFromSessions({ idFormateur: selectedId });
                await loadDetail(selectedId);
                await loadPrestations(selectedId);
                return `${r.created} prestation(s) générée(s)`;
              }, 'Synchronisation des prestations impossible')
            }
            onDownloadPdf={() =>
              void runAction(
                async () => {
                  await downloadFormateurRemunerationPdf(selectedId);
                  return 'PDF d’état de paiement téléchargé';
                },
                'Impossible de générer le PDF',
              )
            }
          />

          <FormateurTarifsEditor
            value={tarifsDraft}
            onChange={setTarifsDraft}
            onSave={() => void saveFormateurTarifs()}
            saving={savingTarifs}
            disabled={!selectedId}
          />

          <FormateurDocumentsCard
            documents={documents}
            onValidate={(docId) =>
              void runAction(async () => {
                await validateAdminFormateurDocument(docId, { statut: 'VALIDE' });
                setDocuments(await fetchAdminFormateurDocuments(selectedId));
                return 'Document validé';
              }, 'Validation du document impossible')
            }
            onReject={(docId) =>
              void runAction(async () => {
                await validateAdminFormateurDocument(docId, {
                  statut: 'REJETE',
                  commentaireAdmin: 'Document non conforme',
                });
                setDocuments(await fetchAdminFormateurDocuments(selectedId));
                return 'Document rejeté';
              }, 'Rejet du document impossible')
            }
          />

          <FormateurProfilReadonlyCard
            prenom={profil?.prenom}
            nom={profil?.nom}
            email={utilisateur?.email}
            telephone={utilisateur?.telephone}
            photoUrl={photoUrl || undefined}
            specialite={String(pf?.specialite ?? '')}
            experienceAnnees={Number(pf?.experienceAnnees ?? 0)}
            biographie={String(pf?.biographie ?? '')}
            urlCv={pf?.urlCv ? String(pf.urlCv) : undefined}
            cvApiPath={
              selectedId && pf?.urlCv
                ? `/admin/formateurs/${encodeURIComponent(selectedId)}/cv`
                : null
            }
            diplomes={viewDiplomes}
            disponibilites={viewDisponibilites}
            referencements={viewReferencements}
            onValidateReferencement={(refId) =>
              void runAction(async () => {
                await validateAdminFormateurReferencement(selectedId, refId, { statut: 'VALIDE' });
                await loadDetail(selectedId);
                return 'Référencement validé';
              }, 'Validation du référencement impossible')
            }
            onRejectReferencement={(refId) =>
              void runAction(async () => {
                await validateAdminFormateurReferencement(selectedId, refId, {
                  statut: 'REJETE',
                  commentaireAdmin: 'Référence non confirmée',
                });
                await loadDetail(selectedId);
                return 'Référencement rejeté';
              }, 'Rejet du référencement impossible')
            }
          />

          {evaluations.length > 0 ? (
            <div className="rounded-xl border p-4">
              <h3 className="font-bold">Dernières évaluations apprenants</h3>
              <ul className="mt-3 max-h-48 space-y-2 overflow-y-auto text-sm">
                {evaluations.slice(0, 10).map((ev) => (
                  <li key={ev.id} className="flex justify-between rounded-lg border px-3 py-2">
                    <span>
                      {ev.apprenantLabel ?? 'Apprenant'} ·{' '}
                      {new Date(ev.dateCreation).toLocaleDateString('fr-FR')}
                    </span>
                    <span className="font-bold text-primary">
                      P {ev.notePonctualite}/5 · Ped {ev.notePedagogie}/5
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {impact?.cohortes && impact.cohortes.length > 0 ? (
            <FormateurImpactCard
              cohortes={impact.cohortes}
              moyenneGlobaleSur20={impact.synthese?.moyenneGlobaleSur20 ?? null}
            />
          ) : null}
        </div>
      ) : tab === 'fiche' ? (
        <p className="text-sm text-on-surface-variant">Sélectionnez un formateur dans l&apos;annuaire.</p>
      ) : null}

      {tab === 'prestations' ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border bg-amber-50/50 p-3">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Montant dû total</p>
              <p className="text-lg font-black text-primary">
                {prestations
                  .filter((p) => p.statut === 'PLANIFIEE' || p.statut === 'REALISEE')
                  .reduce((sum, p) => sum + Number(p.montantTotalCdf), 0)
                  .toLocaleString('fr-FR')}{' '}
                CDF
              </p>
            </div>
            <div className="rounded-xl border bg-sky-50/50 p-3">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Vacations dues</p>
              <p className="text-lg font-black">
                {prestations
                  .filter(
                    (p) =>
                      (p.statut === 'PLANIFIEE' || p.statut === 'REALISEE') && !p.vacationPayee,
                  )
                  .reduce((sum, p) => sum + Number(p.montantVacationCdf ?? 0), 0)
                  .toLocaleString('fr-FR')}{' '}
                CDF
              </p>
            </div>
            <div className="rounded-xl border bg-slate-50 p-3">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Transports dus</p>
              <p className="text-lg font-black">
                {prestations
                  .filter(
                    (p) =>
                      (p.statut === 'PLANIFIEE' || p.statut === 'REALISEE') && !p.transportPaye,
                  )
                  .reduce((sum, p) => sum + Number(p.primeTransportCdf ?? 0), 0)
                  .toLocaleString('fr-FR')}{' '}
                CDF
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-dashed border-outline-variant/50 bg-surface-container-low/40 p-4 text-sm text-on-surface-variant">
            <p className="font-bold text-on-surface">Deux modes de règlement distincts</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                <strong className="text-on-surface">Portefeuille</strong> — crédite le solde Academia du
                formateur (il pourra retirer via Mobile Money). Un mouvement wallet est enregistré.
              </li>
              <li>
                <strong className="text-on-surface">Espèces / virement</strong> — indique que vous avez réglé
                hors plateforme (cash en main, virement bancaire…). Aucun crédit wallet : la prestation est
                seulement soldée comptablement.
              </li>
            </ul>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <button
              type="button"
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-bold text-white"
              onClick={() =>
                void runAction(async () => {
                  const r = await syncPrestationsFromSessions({});
                  await loadPrestations();
                  return `${r.created} prestation(s) créée(s)`;
                }, 'Synchronisation globale impossible')
              }
            >
              Sync toutes les sessions
            </button>
            {selectedPrestationIds.length > 0 ? (
              <>
                <button
                  type="button"
                  className="rounded-lg border px-3 py-1.5 text-sm font-bold"
                  onClick={() =>
                    void runAction(async () => {
                      await markPrestationsRealisees(selectedPrestationIds);
                      await loadPrestations();
                      return 'Prestations marquées comme réalisées';
                    }, 'Impossible de marquer les prestations réalisées')
                  }
                >
                  Marquer réalisées
                </button>

                <span className="hidden h-8 w-px bg-outline-variant/40 sm:inline" aria-hidden />

                <button
                  type="button"
                  className="rounded-lg border border-emerald-600 bg-emerald-50 px-3 py-1.5 text-sm font-bold text-emerald-800"
                  onClick={() =>
                    void runAction(async () => {
                      const r = await payPrestationsViaWallet(selectedPrestationIds);
                      await reloadPrestationsEtSynthese();
                      return `${r.totalPrestations} prestation(s) créditée(s) au portefeuille`;
                    }, 'Crédit portefeuille impossible')
                  }
                >
                  Créditer tout — portefeuille
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-sky-600 px-3 py-1.5 text-sm font-bold text-sky-700"
                  onClick={() =>
                    void runAction(async () => {
                      const ids = prestationIdsForComposante('vacation');
                      if (!ids.length) {
                        throw new Error('Aucune vacation due à régler');
                      }
                      const r = await payPrestationComponentsViaWallet(ids, 'vacation');
                      await reloadPrestationsEtSynthese();
                      return `${r.updated ?? r.totalPrestations ?? ids.length} vacation(s) créditée(s) au portefeuille`;
                    }, 'Crédit vacation impossible')
                  }
                >
                  Vacation → portefeuille
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-sky-600 px-3 py-1.5 text-sm font-bold text-sky-700"
                  onClick={() =>
                    void runAction(async () => {
                      const ids = prestationIdsForComposante('transport');
                      if (!ids.length) {
                        throw new Error('Aucun transport dû à régler');
                      }
                      const r = await payPrestationComponentsViaWallet(ids, 'transport');
                      await reloadPrestationsEtSynthese();
                      return `${r.updated ?? r.totalPrestations ?? ids.length} transport(s) crédité(s) au portefeuille`;
                    }, 'Crédit transport impossible')
                  }
                >
                  Transport → portefeuille
                </button>

                <span className="hidden h-8 w-px bg-outline-variant/40 sm:inline" aria-hidden />

                <label className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-bold uppercase text-on-surface-variant">
                    Règlement hors wallet
                  </span>
                  <AcademiaSelect
                    fieldSize="sm"
                    className="min-w-[9rem]"
                    value={horsWalletMode}
                    onChange={(e) =>
                      setHorsWalletMode(e.target.value as 'ESPECES' | 'VIREMENT_BANCAIRE')
                    }
                  >
                    <option value="ESPECES">Espèces</option>
                    <option value="VIREMENT_BANCAIRE">Virement bancaire</option>
                  </AcademiaSelect>
                </label>
                <button
                  type="button"
                  className="rounded-lg border border-amber-600 bg-amber-50 px-3 py-1.5 text-sm font-bold text-amber-900"
                  onClick={() =>
                    void runAction(async () => {
                      await markPrestationsPayees(selectedPrestationIds, horsWalletMode);
                      await reloadPrestationsEtSynthese();
                      return `Prestations enregistrées payées (${horsWalletMode === 'ESPECES' ? 'espèces' : 'virement'})`;
                    }, 'Enregistrement paiement impossible')
                  }
                >
                  Tout payer — hors wallet
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-amber-700 px-3 py-1.5 text-sm font-bold text-amber-900"
                  onClick={() =>
                    void runAction(async () => {
                      const ids = prestationIdsForComposante('vacation');
                      if (!ids.length) {
                        throw new Error('Aucune vacation due à enregistrer');
                      }
                      const r = await markPrestationComponentsPayees(
                        ids,
                        'vacation',
                        horsWalletMode,
                      );
                      await reloadPrestationsEtSynthese();
                      return `${r.updated ?? r.totalPrestations ?? ids.length} vacation(s) enregistrée(s) (${horsWalletMode === 'ESPECES' ? 'espèces' : 'virement'})`;
                    }, 'Enregistrement vacation impossible')
                  }
                >
                  Vacation — hors wallet
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-amber-700 px-3 py-1.5 text-sm font-bold text-amber-900"
                  onClick={() =>
                    void runAction(async () => {
                      const ids = prestationIdsForComposante('transport');
                      if (!ids.length) {
                        throw new Error('Aucun transport dû à enregistrer');
                      }
                      const r = await markPrestationComponentsPayees(
                        ids,
                        'transport',
                        horsWalletMode,
                      );
                      await reloadPrestationsEtSynthese();
                      return `${r.updated ?? r.totalPrestations ?? ids.length} transport(s) enregistré(s) (${horsWalletMode === 'ESPECES' ? 'espèces' : 'virement'})`;
                    }, 'Enregistrement transport impossible')
                  }
                >
                  Transport — hors wallet
                </button>
              </>
            ) : null}
          </div>
          <div className="overflow-x-auto rounded-xl border">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-container-low text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Formateur</th>
                <th className="px-3 py-2 text-left">Session</th>
                <th className="px-3 py-2 text-right">Heures</th>
                <th className="px-3 py-2 text-right">Vacation CDF</th>
                <th className="px-3 py-2 text-right">Transport CDF</th>
                <th className="px-3 py-2 text-right">Total CDF</th>
                <th className="px-3 py-2">Paiement</th>
                <th className="px-3 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {prestations.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-on-surface-variant">
                    Aucune prestation — assignez formateur + date dans le planning puis « Sync ».
                  </td>
                </tr>
              ) : (
                prestations.map((p) => (
                  <tr key={p.id} className="border-t">
                    <td className="px-3 py-2">
                      {new Date(p.datePrestation).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-3 py-2">
                      {[p.formateurUser?.profil?.prenom, p.formateurUser?.profil?.nom]
                        .filter(Boolean)
                        .join(' ')}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {p.session?.vacation?.libelle} · {p.session?.filiere?.libelle}
                    </td>
                    <td className="px-3 py-2 text-right">{p.heuresPrestees}h</td>
                    <td className="px-3 py-2 text-right">
                      {Number(p.montantVacationCdf ?? 0).toLocaleString('fr-FR')}
                      {p.vacationPayee ? (
                        <span className="block text-[10px] font-bold text-emerald-700">Payée</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {Number(p.primeTransportCdf).toLocaleString('fr-FR')}
                      {p.transportPaye ? (
                        <span className="block text-[10px] font-bold text-emerald-700">Payé</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">
                      {Number(p.montantTotalCdf).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span
                        className={
                          p.vacationPayee ? 'text-emerald-700' : 'text-on-surface-variant'
                        }
                      >
                        Vac.{' '}
                        {p.vacationPayee
                          ? `✓${p.modePaiementVacationLabel ? ` (${p.modePaiementVacationLabel})` : ''}`
                          : '—'}
                      </span>
                      <span className="mx-1 text-on-surface-variant">·</span>
                      <span
                        className={
                          p.transportPaye ? 'text-emerald-700' : 'text-on-surface-variant'
                        }
                      >
                        Transp.{' '}
                        {p.transportPaye
                          ? `✓${p.modePaiementTransportLabel ? ` (${p.modePaiementTransportLabel})` : ''}`
                          : '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-bold">{p.statut}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      ) : null}
    </AdminPageShell>
  );
}
